import React from 'react'
import dayjs from 'dayjs'
import {
  TableBody,
} from 'src/components/TableComponents'
import { NumberFmt, measureUnit } from 'src/utils'
import { useTheme } from 'styled-components'
import * as S from '../styles'
import { ReceivementProduct, OverviewProductStatusEnum, ProductStatusEnum } from '../../../ReceivementTypes'
import { ReceivementProductWrapper } from '../../ReceivementProductWrapper'

interface Props {
  product: ReceivementProduct
}

const DetailProductTable = ({ product }: Props) => {
  const theme = useTheme()
  const rejectedArQuantity = product.rejectedArQuantity ?? 0

  const getDifference = () => {
    const value = product.usefulQuantity - product.expectedQuantity
    const prefix = value > 0 ? '+' : ''
    const color = value > 0 ? theme.colors.confirm : value < 0 ? theme.colors.red : 'inerit'

    return <S.Text style={{ color }}><NumberFmt prefix={prefix} value={value} sufix={`${measureUnit(product.isKg)}`} /></S.Text>
  }

  const getTotal = () => {
    const value = product.damageQuantity + product.usefulQuantity

    return <NumberFmt value={value} sufix={`${measureUnit(product.isKg)}`} />
  }

  const getTotalRejected = () => {
    const value = (product.damageQuantity + product.usefulQuantity) - product.acceptedQuantity

    return <NumberFmt value={value} sufix={`${measureUnit(product.isKg)}`} />
  }

  const getValidateByBatch = (batch) => {
    const verifyExpirationDate = batch.minExpirationDate && batch.expirationDate && batch.expirationDate < batch.minExpirationDate
    const minExpirationDateEmpty = batch.minExpirationDate === null || batch.minExpirationDate === '' || !batch.minExpirationDate
    const emptyExpirationDate = batch.expirationDate === null || batch.expirationDate === '' || !batch.expirationDate

    const expirationDate = batch.expirationDate ? dayjs(batch.expirationDate).format('DD/MM/YYYY') : '---'
    const backgroundColor = (!emptyExpirationDate && dayjs(batch.expirationDate) < dayjs(batch.createdAtDate))
      ? theme.colors.red
      : verifyExpirationDate
        ? theme.colors.orange
        : emptyExpirationDate || (batch.salesData === null && minExpirationDateEmpty)
          ? 'inerit'
          : (batch.salesData === true && minExpirationDateEmpty) || (batch.salesData === false && minExpirationDateEmpty)
            ? theme.colors.black
            : 'inerit'
    const color = emptyExpirationDate || (batch.salesData === null && minExpirationDateEmpty)
      ? theme.colors.darkGray
      : (verifyExpirationDate) || (batch.salesData === true && minExpirationDateEmpty) || (batch.salesData === false && minExpirationDateEmpty)
        ? theme.colors.white
        : theme.colors.darkGray
    return <S.ExpirationDate style={{ backgroundColor, color }}>{expirationDate}</S.ExpirationDate>
  }

  const validateExpirationDate = (batch) => {
    const verifyExpirationDate = batch.minExpirationDate && batch.expirationDate && batch.expirationDate < batch.minExpirationDate
    const minExpirationDateEmpty = batch.minExpirationDate === null || batch.minExpirationDate === '' || !batch.minExpirationDate
    const emptyExpirationDate = batch.expirationDate === null || batch.expirationDate === '' || !batch.expirationDate

    if (!emptyExpirationDate && dayjs(batch.expirationDate) < dayjs(batch.createdAtDate)) {
      return 'Data de validade vencida'
    }

    if (emptyExpirationDate || (batch.salesData === null && minExpirationDateEmpty)) {
      return ''
    }

    if (batch.salesData === true && minExpirationDateEmpty) {
      return 'SEM VENDAS'
    }

    if (verifyExpirationDate) {
      const formattedMinExpirationDate = dayjs(batch.minExpirationDate).format('DD/MM/YYYY')
      return `A data de validade mínima calculada pelo sistema para este lote é: ${formattedMinExpirationDate}`
    }

    if (batch.salesData === false && minExpirationDateEmpty) {
      return 'Não foi possível calcular a data de validade mínima para este lote'
    }
    return ''
  }

  const getProductInfo = () => `
    ${product.productDescription}
    (Modelo: ${product.productCode},
    Caixaria: ${product.quantityPerBox} ${measureUnit(product.isKg).toLocaleLowerCase()})
  `

  return (
    <ReceivementProductWrapper status={product.status === OverviewProductStatusEnum.CORRECT ? ProductStatusEnum.CORRECT : ProductStatusEnum.DIVERGENT} buyer={product.buyerName} orderBuyerName={product.orderBuyerName}>
      <S.Table>
        <S.TableHeader
          columnInformation={[
            { columnName: 'AR' },
            { columnName: 'NF' },
            { columnName: 'Produto' },
          ]}
          flexColumns={[
            0.06,
            0.06,
            0.88,
          ]}
        />
        <TableBody>
          <S.TableBodyRow backgroundColor={theme.colors.white}>
            <S.TableBodyCell flex={0.06}>{product.idReceivementAuthorization}</S.TableBodyCell>
            <S.TableBodyCell flex={0.06}>{product.invoiceNumber}</S.TableBodyCell>
            <S.TableBodyCell flex={0.88}>{getProductInfo()}</S.TableBodyCell>
          </S.TableBodyRow>
        </TableBody>
        <S.TableHeader
          backgroundColor={theme.colors.gray}
          columnInformation={[
            { columnName: 'Lote' },
            { columnName: 'Qtd. Recebida' },
            { columnName: 'Qtd. Avariada' },
            { columnName: 'Qtd. Útil' },
            { columnName: 'Qtd. Rejeitada' },
            { columnName: 'Data Validade' },
            { columnName: 'Ação' },
            { columnName: 'Motivo Avaria' },
          ]}
          flexColumns={[
            0.75,
            1,
            1.25,
            1.25,
            1.25,
            1.25,
            1.25,
            1,
          ]}
        />
        <TableBody>
          {product.batches.map((batch, index) => (
            <S.TableBodyRow key={`${batch.idBatch}-${index}`} backgroundColor={theme.colors.white}>
              <S.TableBodyCell flex={0.75}>{batch.idBatch}</S.TableBodyCell>
              <S.TableBodyCell flex={1}>
                <NumberFmt value={batch.totalQuantity} sufix={measureUnit(batch.measureUnit.isKg)} />
              </S.TableBodyCell>
              <S.TableBodyCell flex={1.25}>
                <NumberFmt value={batch.damageQuantity} sufix={measureUnit(batch.measureUnit.isKg)} />
              </S.TableBodyCell>
              <S.TableBodyCell flex={1.25}>
                <NumberFmt value={batch.usefulQuantity} sufix={measureUnit(batch.measureUnit.isKg)} />
              </S.TableBodyCell>
              <S.TableBodyCell flex={1.25}>
                <NumberFmt value={batch.totalQuantity - batch.acceptedQuantity} sufix={measureUnit(batch.measureUnit.isKg)} />
              </S.TableBodyCell>
              <S.TableBodyCell
                flex={1.25}
                tooltipMessage={validateExpirationDate(batch)}
              >
                {getValidateByBatch(batch)}
              </S.TableBodyCell>
              <S.TableBodyCell flex={1.25}>
                {batch.selectedAction ? batch.selectedAction : 'Não informado'}
              </S.TableBodyCell>
              <S.TableBodyCell flex={1}>{batch.damageType}</S.TableBodyCell>
            </S.TableBodyRow>
          ))}
        </TableBody>
        <S.TableHeader
          backgroundColor={theme.colors.gray}
          columnInformation={[
            { columnName: 'Total Esperado' },
            { columnName: 'Total Recebido' },
            { columnName: 'Total Avariado' },
            { columnName: 'Total Útil' },
            { columnName: 'Total Aceito' },
            { columnName: 'Total Rejeitado' },
            {
              columnName: 'Total Rejeitado (AR)',
              tooltipMessage: 'Quantidade a devolver solicitada durante a conferência da nota.',
              alert: rejectedArQuantity > 0,
            },
            {
              columnName: 'Total Rejeitado (Excesso)',
              tooltipMessage: 'Quantidade recebida acima da nota fiscal e devolvida ao fornecedor.',
            },
            { columnName: 'Útil - Esperado' },
          ]}
          flexColumns={[
            0.75,
            1,
            1.25,
            1.25,
            1.25,
            1.25,
            1.25,
            1.25,
            1,
          ]}
        />
        <S.TableBody>
          <S.TableBodyRow backgroundColor={theme.colors.white}>
            <S.TableBodyCell flex={0.75}><NumberFmt value={product.expectedQuantity} sufix={measureUnit(product.isKg)} /></S.TableBodyCell>
            <S.TableBodyCell flex={1}>{getTotal()}</S.TableBodyCell>
            <S.TableBodyCell flex={1.25}>
              <NumberFmt value={product.damageQuantity} sufix={measureUnit(product.isKg)} />
            </S.TableBodyCell>
            <S.TableBodyCell flex={1.25}>
              <NumberFmt value={product.usefulQuantity} sufix={measureUnit(product.isKg)} />
            </S.TableBodyCell>
            <S.TableBodyCell flex={1.25}>
              <NumberFmt value={product.acceptedQuantity} sufix={measureUnit(product.isKg)} />
            </S.TableBodyCell>
            <S.TableBodyCell flex={1.25}>{getTotalRejected()}</S.TableBodyCell>
            <S.TableBodyCell flex={1.25}>
              <S.NumberText isNegative={rejectedArQuantity > 0} data-testid='rejected-ar-quantity'>
                <NumberFmt value={rejectedArQuantity} sufix={measureUnit(product.isKg)} />
              </S.NumberText>
            </S.TableBodyCell>
            <S.TableBodyCell flex={1.25}>
              <NumberFmt value={product.rejectedExcessQuantity ?? 0} sufix={measureUnit(product.isKg)} />
            </S.TableBodyCell>
            <S.TableBodyCell flex={1}>{getDifference()}</S.TableBodyCell>
          </S.TableBodyRow>
        </S.TableBody>
      </S.Table>
    </ReceivementProductWrapper>
  )
}

export { DetailProductTable }
